import React, { useRef, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import { editPost, replacePostMedia } from "../../../api";
import { getActiveAwareAuthToken } from "../../../auth/bootstrapTokens";
import { withPostMediaRefreshToken } from "../../../utils/postMedia";
import useCreatePostFormResources from "../../CreatePostModal/useCreatePostFormResources";
import "../../../styles/components/profile/EditPostModal.css";
import EditCatchPostForm from "./EditCatchPostForm";
import EditOpinionPostForm from "./EditOpinionPostForm";
import EditPostMediaReplacement from "./EditPostMediaReplacement";
import { hasEditPostPayloadChanged } from "./editPostChangeDetection";
import {
  buildEditPostPayload,
  getEditablePostType,
} from "./editPostPayloadBuilder";
import useEditCatchPostFormState from "./useEditCatchPostFormState";
import useEditPostMediaReplacement from "./useEditPostMediaReplacement";
import useEditPostFormState from "./useEditPostFormState";

const ERROR_KEY_FALLBACKS = {
  invalid_post_media_upload: "post_edit_media_invalid",
  post_media_replace_forbidden: "post_edit_media_auth_failed",
  post_media_replace_unsupported_post_type: "post_edit_unsupported_post_type",
  post_media_replace_temporarily_unavailable: "post_edit_media_replace_failed",
};

const getErrorKey = (error, fallbackKey) => {
  const detailKey =
    typeof error?.detail === "string" && error.detail
      ? error.detail.split(":")[0].trim()
      : "";

  return ERROR_KEY_FALLBACKS[detailKey] || detailKey || fallbackKey;
};

export default function EditPostModal({ post, onClose, onSaved }) {
  const { t } = useTranslation();
  const editableType = getEditablePostType(post);
  const resources = useCreatePostFormResources({
    enabled: editableType === "catch",
  });
  const opinionForm = useEditPostFormState(post);
  const catchForm = useEditCatchPostFormState(post, resources, {
    pickedCoordinates: post?._mapPickedCoordinates,
    selectionId: post?._mapPickRequestedAt,
  });
  const mediaReplacement = useEditPostMediaReplacement(post);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const saveLockRef = useRef(false);
  const isUnsupported = Boolean(post) && !editableType;

  if (!post) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saveLockRef.current || isUnsupported) return;

    if (editableType === "catch" && catchForm.fishSpeciesErrorKey) {
      setErrorMessage(
        t(catchForm.fishSpeciesErrorKey, {
          defaultValue:
            "Please enter the fish name using English/Latin letters.",
        })
      );
      return;
    }

    const payload = buildEditPostPayload(
      post,
      editableType === "catch"
        ? catchForm.buildPayloadFormState()
        : opinionForm.formState
    );
    if (!payload) {
      setErrorMessage(t("post_edit_unsupported_post_type"));
      return;
    }
    if (mediaReplacement.errorKey) {
      setErrorMessage(t(mediaReplacement.errorKey));
      return;
    }

    const token = getActiveAwareAuthToken(window);

    const comparisonPost = post?._editComparisonPost || post;
    const hasMetadataChange = hasEditPostPayloadChanged(comparisonPost, payload);
    const hasMediaChange = mediaReplacement.hasSelection;
    if (!hasMetadataChange && !hasMediaChange) {
      onClose?.();
      return;
    }

    saveLockRef.current = true;
    setIsSaving(true);
    setErrorMessage("");

    let nextPost = post;
    let savedMetadataPost = null;

    try {
      if (hasMetadataChange) {
        savedMetadataPost = await editPost(post.id, payload, token || "");
        nextPost = savedMetadataPost || nextPost;
      }

      if (hasMediaChange) {
        const updatedMediaPost = await replacePostMedia(
          post.id,
          mediaReplacement.selectedFile,
          token || ""
        );
        nextPost = withPostMediaRefreshToken(updatedMediaPost || nextPost);
      }

      mediaReplacement.clearSelectedMedia();
      onSaved?.(nextPost);
      onClose?.();
    } catch (error) {
      console.error("Failed to edit post:", error);
      if (savedMetadataPost && hasMediaChange) {
        onSaved?.(savedMetadataPost, { close: false });
        setErrorMessage(t("post_edit_media_partial_failure"));
      } else {
        const fallbackKey = hasMediaChange
          ? "post_edit_media_replace_failed"
          : "post_edit_save_failed";
        const errorKey = getErrorKey(error, fallbackKey);
        setErrorMessage(
          t(errorKey, {
            defaultValue: t(fallbackKey),
          })
        );
      }
    } finally {
      saveLockRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <div className="edit-post-modal-overlay" role="presentation">
      <div
        className="edit-post-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-post-modal-title"
      >
        <div className="edit-post-modal-header">
          <h2 id="edit-post-modal-title">{t("post_edit_title")}</h2>
          <button
            type="button"
            className="edit-post-icon-btn"
            onClick={onClose}
            aria-label={t("close")}
            disabled={isSaving}
          >
            <FaTimes />
          </button>
        </div>

        {isUnsupported ? (
          <div className="edit-post-unsupported">
            {t("post_edit_unsupported_post_type")}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <fieldset className="edit-post-fieldset" disabled={isSaving}>
              {editableType === "catch" ? (
                <EditCatchPostForm
                  catchForm={catchForm}
                  disabled={isSaving}
                />
              ) : (
                <EditOpinionPostForm
                  t={t}
                  formState={opinionForm.formState}
                  setCaption={opinionForm.setCaption}
                  disabled={isSaving}
                />
              )}
              <EditPostMediaReplacement
                post={post}
                mediaReplacement={mediaReplacement}
                t={t}
                disabled={isSaving}
              />
            </fieldset>

            {errorMessage && (
              <div className="edit-post-error" role="alert">
                {errorMessage}
              </div>
            )}

            <div className="edit-post-actions">
              <button
                type="button"
                className="edit-post-secondary-btn"
                onClick={onClose}
                disabled={isSaving}
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                className="edit-post-primary-btn"
                disabled={isSaving}
                aria-busy={isSaving}
              >
                {isSaving ? t("post_edit_saving") : t("save")}
              </button>
            </div>
          </form>
        )}

        {isUnsupported && (
          <div className="edit-post-actions">
            <button
              type="button"
              className="edit-post-secondary-btn"
              onClick={onClose}
            >
              {t("close")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
